import { router as inertiaRouter } from '@inertiajs/react';
import type {
  CompileAiComponentOptions,
  CompiledAiComponent,
  PageRuntimeSourceKind
} from '@Pages/pageRuntime/compiler/compileAiComponent';
import type { PageSdkRequestTracker } from '@Pages/pageRuntime/sdk/requestActivity';
import type { RouterLike } from '@Pages/pageRuntime/sdk/router';
import type { SdkContract } from '@Pages/pageRuntime/sdk/types';
import type { AuthoringRuntime } from '@Pages/pageRuntime/useEditorPreviewGeneration';
import { createContext, useContext } from 'react';

/**
 * Where a surface's JSX transform runs. Authoring previews use a per-document
 * Web Worker so typing never waits on the compiler; published pages and token
 * draft previews transform once, on the thread that renders them.
 */
export type PageTransformMode = 'main-thread' | 'worker';

type PageRuntimeBase = {
  Sdk: SdkContract;
  requestTracker: PageSdkRequestTracker;
  router: RouterLike;
};

/**
 * Discriminated on `transformMode`, so a consumer can only reach the half of
 * the runtime its surface actually has: a published page has a synchronous
 * `compile`, an authoring preview has the generation its worker committed.
 */
export type PageRuntime = PageRuntimeBase &
  (
    | {
        transformMode: 'main-thread';
        compile: (
          source: string | null | undefined,
          kind: PageRuntimeSourceKind,
          options?: Omit<CompileAiComponentOptions, 'kind'>
        ) => CompiledAiComponent;
      }
    | { transformMode: 'worker'; authoring: AuthoringRuntime }
  );

export const PageRuntimeContext = createContext<PageRuntime | null>(null);

export function usePageRuntime(): PageRuntime | null {
  return useContext(PageRuntimeContext);
}

/**
 * The router that built-in components send navigation and auth requests
 * through, so a host that swaps the runtime router also covers them.
 */
export function usePageRouter(): RouterLike {
  return useContext(PageRuntimeContext)?.router ?? inertiaRouter;
}
